﻿
import { ImageCanvas } from 'ol';
import olRasterSource, { Options } from 'ol/source/Raster';
import TileSource from 'ol/source/Tile';
import { Extent } from 'ol/extent';
import Projection from 'ol/proj/Projection';

import ViewUtilities from "../../utilities/ViewUtilities"

// Limit resolution of the raster source to its underlying source.
// Moves the smoothing operation after the raster operation instead of before,
// which causes data as color to be mis-interpolated.
// HACK: overwrites internal getImage method, might break in future versions of 
// openlayers or with more aggressive minification
export interface RasterOptions extends Options {
    buffer?: number;
}
export class RasterSource extends olRasterSource {
    constructor(options: RasterOptions) {
        super(options);
        if (!options.sources || options.sources.length !== 1) {
            throw new Error("RasterSource supports only one source");
        }
        this.source = options.sources[0] as TileSource;
        if (!(this.source instanceof TileSource)) {
            throw new Error("RasterSource supports only tile sources");
        } else {
            // Force a refresh of the raster after some delay once all tiles are done loading
            // Fixes an openlayers bug where tiles are sometimes left unrendered
            let numLoading = 0;
            this.source.on("tileloadstart", () => { numLoading++ });
            this.source.on(["tileloadend", "tileloaderror"], () => {
                if (--numLoading <= 0) {
                    window.setTimeout(() => this.refresh(), 1000);
                }
            });
            this.source.on("propertychange", function () { console.log(arguments) })
        }

        this.getImageBase = (olRasterSource.prototype as any).getImage;
        if (!(this.getImageBase instanceof Function)) {
            throw new Error("ol.source.Raster not compatible");
        }
        this.buffer = options.buffer || 50;
    }
    private readonly source: TileSource;
    public readonly buffer: number;
    private readonly getImageBase: (ext: Extent, vr: number, pr: number, proj: Projection) => ImageCanvas;

    // NOTE: only supports a pixel ratio of 1
    public getImage(renderedExtent: Extent, viewResolution: number, pixelRatio: number, projection: Projection): ImageCanvas {
        // get source max zoom
        const maxZoom = this.source.getTileGrid()?.getMaxZoom() ?? 0;
        // limit resolution to source
        viewResolution = Math.max(viewResolution, ViewUtilities.getResolutionFromZoom(maxZoom));
        // round resolution to match image pixels
        renderedExtent[0] = Math.floor(renderedExtent[0] / viewResolution - this.buffer) * viewResolution;
        renderedExtent[1] = Math.floor(renderedExtent[1] / viewResolution - this.buffer) * viewResolution;
        renderedExtent[2] = Math.ceil(renderedExtent[2] / viewResolution + this.buffer) * viewResolution;
        renderedExtent[3] = Math.ceil(renderedExtent[3] / viewResolution + this.buffer) * viewResolution;
        // call base getImage
        return this.getImageBase(renderedExtent, viewResolution, pixelRatio, projection)
    }
}
